import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { ReportLang } from '@/utils/helper/reportLang'
import type { AxiosInstance } from 'axios'
import dayjs from 'dayjs'
import { deserialize } from 'ts-jackson'
import { FacilityBookedListItem } from '../facility/model'
import { MEMBER_PACKAGE_TYPE, PageInfo, PaginationOpt, PaginationResponse } from '../model'
import { User } from '../user/model'
import {
  ACCOUNT_STATUS,
  AdjustCreditBody,
  AggregateCount,
  CheckInSummary,
  CreateMemberDocumentPayload,
  CreateMemberPayload,
  CreateMemberShipPackagePayload,
  CreateMemberShipPackageResponse,
  DeleteMemberDocumentResponse,
  EditMemberContactPayload,
  EditPersonalProfilePayload,
  MEMBER_STATUS,
  MEMBER_TYPE,
  Member,
  MemberAccountStatusInfo,
  MemberCheckInHistory,
  MemberDocument,
  MemberMonthlyLatestActivity,
  MemberNote,
  MemberOption,
  CollectBulkPayload,
  CollectBulkResult,
  CollectInstallmentPayload,
  DueInstallmentQuery,
  DueInstallmentRow,
  FamilyAllocationInput,
  FamilyPackageDetail,
  FamilySharingMode,
  MemberShipPayload,
  MembershipData,
  MembershipPaymentPlanData,
  PACKAGE_STATUS,
  PaginationOptInactive,
  PaginationResponseMember,
  SaleOwnerChangePreview,
  SaleOwnerOption,
  SwimmingHistory,
  TrainingHistory,
  UpdateAccountStatusPayload,
  UpdateMemberDocumentPayload,
  UserMembershipParam,
  type TrainerChangeCandidate,
  type TrainerChangePreview,
  type TrainerChangeResult,
} from './model'

export const MemberService = (client: AxiosInstance) => {
  return {
    async getMembers(
      opt: PaginationOpt,
      memberType?: MEMBER_TYPE,
      memberStatus?: MEMBER_STATUS,
      packageType?: MEMBER_PACKAGE_TYPE
    ): Promise<Member[]> {
      // GET /members always returns a paginated envelope since backend #707;
      // unpaginated callers (search dropdowns) get the first page. pageSize is
      // capped at 100 server-side.
      const { data } = await wrapApiCall(() =>
        client.get<
          Record<string, unknown>[] | { pagination?: unknown; data?: Record<string, unknown>[] }
        >('/members', {
          // Explicit args only override `opt` when actually provided — an
          // undefined trailing arg must not clobber a filter passed via opt.
          params: {
            pageSize: 50,
            ...opt,
            ...(memberType !== undefined ? { memberType } : {}),
            ...(memberStatus !== undefined ? { memberStatus } : {}),
            ...(packageType !== undefined ? { packageType } : {}),
          },
        })
      )
      const rows = Array.isArray(data) ? data : (data?.data ?? [])
      return rows.map((item: Record<string, unknown>) => deserialize(item, Member))
    },
    async getMembersWithPagination(
      opt: PaginationOpt,
      memberType?: MEMBER_TYPE,
      memberStatus?: MEMBER_STATUS,
      packageType?: MEMBER_PACKAGE_TYPE,
      usePersonalMemberInfoSaleOwner?: boolean,
      packageStatus?: PACKAGE_STATUS,
      sortBy?: string,
      sortOrder?: 'asc' | 'desc',
      tagIds?: number[]
    ): Promise<PaginationResponse<Member>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/members',
          {
            params: {
              ...opt,
              memberType,
              memberStatus,
              packageType,
              usePersonalMemberInfoSaleOwner,
              packageStatus,
              sortBy,
              sortOrder,
              ...(tagIds && tagIds.length > 0 ? { tagIds: tagIds.join(',') } : {}),
            },
          }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, Member)) ?? [],
      }
    },
    /**
     * Choices for the member list's Sales Owner filter (QTY-2073).
     *
     * Deliberately not `/users/staff`: that drops anyone soft-deleted, in
     * another branch, or outside the sales roles, and narrows further for CS
     * & Sales actors — each of which can still own members, leaving the
     * filter unable to reach rows the list already shows. This endpoint
     * returns current sales staff plus everyone who actually owns members.
     */
    async getSaleOwnerOptions(): Promise<SaleOwnerOption[]> {
      const { data } = await wrapApiCall(() =>
        client.get<SaleOwnerOption[] | { data?: SaleOwnerOption[] }>('/members/sale-owners')
      )
      // Bare array today. Unwrapped the same way as `/members`, which went
      // envelope-only in backend #707: if this one ever follows, an envelope
      // would otherwise reach the page as an object and throw on `.map`.
      return Array.isArray(data) ? data : (data?.data ?? [])
    },
    /** Server-generated member workbook — same filters as the list, one request. */
    async downloadMemberListWorkbook(params: {
      query?: string
      memberType?: MEMBER_TYPE
      memberStatus?: MEMBER_STATUS
      packageStatus?: PACKAGE_STATUS
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      tagIds?: number[]
      /** Sales owner filter (QTY-2073) — keeps the export in step with the list. */
      saleId?: string | number
      lang: ReportLang
    }): Promise<{ blob: Blob; total: number; truncated: boolean }> {
      const { tagIds, ...rest } = params
      const response = await wrapApiCall(() =>
        client.get<Blob>('/members/report/excel', {
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          params: {
            ...rest,
            usePersonalMemberInfoSaleOwner: true,
            ...(tagIds && tagIds.length > 0 ? { tagIds: tagIds.join(',') } : {}),
          },
        })
      )
      return {
        blob: response.data,
        total: Number(response.headers['x-export-total'] ?? 0),
        truncated: response.headers['x-export-truncated'] === 'true',
      }
    },
    async getMemberAccountStatus(id: string | number): Promise<MemberAccountStatusInfo> {
      const { data } = await wrapApiCall(() =>
        client.get<Partial<MemberAccountStatusInfo>>(`/members/${id}/account-status`)
      )
      const rawStatus = (data?.status ?? '').toString().toUpperCase() as ACCOUNT_STATUS
      return {
        status:
          rawStatus === ACCOUNT_STATUS.INACTIVE ? ACCOUNT_STATUS.INACTIVE : ACCOUNT_STATUS.ACTIVE,
        hasActivePackage: !!data?.hasActivePackage,
        usedHeadcount: Number(data?.usedHeadcount ?? 0),
        headcountLimit: Number(data?.headcountLimit ?? 0),
      }
    },
    async updateMemberAccountStatus(
      id: string | number,
      payload: UpdateAccountStatusPayload
    ): Promise<MemberAccountStatusInfo> {
      const { data } = await wrapApiCall(() =>
        client.patch<MemberAccountStatusInfo>(`/members/${id}/account-status`, payload)
      )
      return data
    },
    /** B-916: active PTs of the branch a PT package can be given (minus its current one). */
    async getTrainerChangeCandidates(membershipId: number): Promise<TrainerChangeCandidate[]> {
      const { data } = await wrapApiCall(() =>
        client.get<TrainerChangeCandidate[]>(
          `/memberships/${membershipId}/trainer-change/candidates`
        )
      )
      return data
    },
    /** B-916: the upcoming sessions giving the package `trainerId` would cancel (none for null). */
    async previewTrainerChange(
      membershipId: number,
      trainerId: number | null
    ): Promise<TrainerChangePreview> {
      const { data } = await wrapApiCall(() =>
        client.get<TrainerChangePreview>(
          `/memberships/${membershipId}/trainer-change/preview`,
          trainerId != null ? { params: { trainerId } } : undefined
        )
      )
      return data
    },
    /**
     * B-916: give a PT package `trainerId` (cancelling its upcoming sessions with
     * anyone else), or null to remove its fixed PT so any PT may teach it.
     */
    async changeTrainer(
      membershipId: number,
      trainerId: number | null
    ): Promise<TrainerChangeResult> {
      const { data } = await wrapApiCall(() =>
        client.post<TrainerChangeResult>(`/memberships/${membershipId}/trainer-change`, {
          trainerId,
        })
      )
      return data
    },
    async getMembershipById(id: string | number): Promise<MembershipData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/memberships/${id}`)
      )
      return deserialize(data, MembershipData)
    },
    async updateMembership(id: string | number, body: MemberShipPayload): Promise<MembershipData> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/memberships/${id}`, body)
      )
      return deserialize(data, MembershipData)
    },
    async createMember(payload: CreateMemberPayload): Promise<User> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/members', payload)
      )
      return deserialize(data, User)
    },
    // ── Family Package (QTY-2029 / QTY-2030) ────────────────────────────────
    async getFamilyPackage(membershipId: number | string): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.get<FamilyPackageDetail>(`/memberships/${membershipId}/family`)
      )
      return data
    },
    async addFamilyMember(
      membershipId: number | string,
      userId: number
    ): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.post<FamilyPackageDetail>(`/memberships/${membershipId}/family/members`, { userId })
      )
      return data
    },
    async setFamilyMemberEligibility(
      membershipId: number | string,
      userId: number,
      isEligible: boolean
    ): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.patch<FamilyPackageDetail>(`/memberships/${membershipId}/family/members/${userId}`, {
          isEligible,
        })
      )
      return data
    },
    async removeFamilyMember(
      membershipId: number | string,
      userId: number
    ): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.delete<FamilyPackageDetail>(`/memberships/${membershipId}/family/members/${userId}`)
      )
      return data
    },
    async topUpFamilyMember(
      membershipId: number | string,
      userId: number,
      amount: number
    ): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.post<FamilyPackageDetail>(
          `/memberships/${membershipId}/family/members/${userId}/top-up`,
          { amount }
        )
      )
      return data
    },
    /**
     * QTY-2087: set several members' allocations at once (up or down). Sending
     * the whole set in one call lets the server validate the totals before any
     * of it lands.
     */
    async setFamilyAllocations(
      membershipId: number | string,
      allocations: FamilyAllocationInput[]
    ): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.patch<FamilyPackageDetail>(`/memberships/${membershipId}/family/allocations`, {
          allocations,
        })
      )
      return data
    },
    async switchFamilySharingMode(
      membershipId: number | string,
      sharingMode: FamilySharingMode
    ): Promise<FamilyPackageDetail> {
      const { data } = await wrapApiCall(() =>
        client.patch<FamilyPackageDetail>(`/memberships/${membershipId}/family/mode`, {
          sharingMode,
        })
      )
      return data
    },
    async getMembership(params: UserMembershipParam): Promise<MembershipData[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>('/memberships', { params: params })
      )
      return data?.map((item: Record<string, unknown>) => deserialize(item, MembershipData)) ?? []
    },
    async getMembershipPaymentPlans(userId: string | number): Promise<MembershipPaymentPlanData[]> {
      const { data } = await wrapApiCall(() =>
        client.get<MembershipPaymentPlanData[]>('/memberships/payment-plans', {
          params: { userId },
        })
      )
      return data ?? []
    },
    async collectInstallment(
      installmentId: number,
      body: CollectInstallmentPayload
    ): Promise<{ success: boolean; receiptCode: string }> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success: boolean; receiptCode: string }>(
          `/memberships/installments/${installmentId}/collect`,
          body
        )
      )
      return data
    },
    async collectInstallmentsBulk(body: CollectBulkPayload): Promise<CollectBulkResult> {
      const { data } = await wrapApiCall(() =>
        client.post<CollectBulkResult>('/memberships/installments/collect-bulk', body)
      )
      return data
    },
    async getDueInstallments({
      status,
      page = 1,
      pageSize = 10,
      sortBy,
      sortOrder,
      query,
      dueFrom,
      dueTo,
    }: DueInstallmentQuery = {}): Promise<PaginationResponse<DueInstallmentRow>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: DueInstallmentRow[] }>(
          '/memberships/installments/due',
          {
            params: {
              ...(status ? { status } : {}),
              page,
              pageSize,
              ...(sortBy ? { sortBy } : {}),
              ...(sortOrder ? { sortOrder } : {}),
              ...(query ? { query } : {}),
              ...(dueFrom ? { dueFrom } : {}),
              ...(dueTo ? { dueTo } : {}),
            },
          }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data ?? [],
      }
    },
    async markInstallmentFailed(installmentId: number): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success: boolean }>(`/memberships/installments/${installmentId}/fail`)
      )
      return data
    },
    async suspendPaymentPlan(planId: number): Promise<{ success: boolean; affected: number }> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success: boolean; affected: number }>(
          `/memberships/payment-plans/${planId}/suspend`
        )
      )
      return data
    },
    async reactivatePaymentPlan(planId: number): Promise<{ success: boolean; affected: number }> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success: boolean; affected: number }>(
          `/memberships/payment-plans/${planId}/reactivate`
        )
      )
      return data
    },
    async getPtRemainingSessionsCount(params?: { asOfDate?: string }): Promise<{ count: number }> {
      const { data } = await wrapApiCall(() =>
        client.get<{ count?: number }>('/memberships/remaining-sessions/count', { params })
      )
      return { count: Number(data?.count ?? 0) }
    },
    async getSwimmingRemainingSessionsCount(params?: {
      asOfDate?: string
    }): Promise<{ count: number }> {
      const { data } = await wrapApiCall(() =>
        client.get<{ count?: number }>('/memberships/remaining-swimming-sessions/count', { params })
      )
      return { count: Number(data?.count ?? 0) }
    },
    async getPtRemainingSessionsByLatestTrainer(params?: { asOfDate?: string }): Promise<
      Array<{
        memberId?: number
        memberNo: string
        memberName: string
        trainerId?: number
        trainerName: string
        remainingSessions: number
      }>
    > {
      const { data } = await wrapApiCall(() =>
        client.get<
          Array<{
            memberId?: number | string | null
            memberNo?: string | null
            memberName?: string | null
            trainerId?: number | string | null
            trainerName?: string | null
            remainingSessions?: number | string | null
          }>
        >('/memberships/remaining-sessions/by-latest-trainer', {
          params,
        })
      )
      return Array.isArray(data)
        ? data.map((item) => ({
            memberId: Number.isFinite(Number(item?.memberId)) ? Number(item?.memberId) : undefined,
            memberNo: String(item?.memberNo ?? '-'),
            memberName: String(item?.memberName ?? '-'),
            trainerId: Number.isFinite(Number(item?.trainerId))
              ? Number(item?.trainerId)
              : undefined,
            trainerName: String(item?.trainerName ?? '-'),
            remainingSessions: Number(item?.remainingSessions ?? 0),
          }))
        : []
    },
    async getSwimmingRemainingSessionsByLatestCoach(params?: { asOfDate?: string }): Promise<
      Array<{
        memberId?: number
        memberNo: string
        memberName: string
        trainerId?: number
        trainerName: string
        remainingSessions: number
      }>
    > {
      const { data } = await wrapApiCall(() =>
        client.get<
          Array<{
            memberId?: number | string | null
            memberNo?: string | null
            memberName?: string | null
            trainerId?: number | string | null
            trainerName?: string | null
            remainingSessions?: number | string | null
          }>
        >('/memberships/remaining-swimming-sessions/by-latest-coach', { params })
      )
      return Array.isArray(data)
        ? data.map((item) => ({
            memberId: Number.isFinite(Number(item?.memberId)) ? Number(item?.memberId) : undefined,
            memberNo: String(item?.memberNo ?? '-'),
            memberName: String(item?.memberName ?? '-'),
            trainerId: Number.isFinite(Number(item?.trainerId))
              ? Number(item?.trainerId)
              : undefined,
            trainerName: String(item?.trainerName ?? '-'),
            remainingSessions: Number(item?.remainingSessions ?? 0),
          }))
        : []
    },
    async getCheckInSummary(id: string | number): Promise<CheckInSummary> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/member/${id}/check-in/summary`)
      )
      return deserialize(data, CheckInSummary)
    },
    async createMemberNote(userId: number, note: string): Promise<MemberNote[]> {
      const { data } = await wrapApiCall(() =>
        client.post<MemberNote[]>(`/member-note`, { memberId: userId, note: note })
      )
      return data
    },
    async getMemberNote(userId: string | number): Promise<MemberNote[]> {
      const { data } = await wrapApiCall(() =>
        client.get<MemberNote[]>(`/member-note`, { params: { userId: userId } })
      )
      return data
    },
    async updateMemberNote(note: string, id: string | number): Promise<{ note: string }> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ note?: string }>(`/members/${id}/note`, { note })
      )
      return { note: String(data?.note) }
    },
    async updateMemberNoteById(note: string, id: string | number): Promise<MemberNote> {
      const { data } = await wrapApiCall(() =>
        client.patch<MemberNote>(`/member-note/${id}`, { note })
      )
      return data
    },
    async updateMemberProfile(
      personalData: EditPersonalProfilePayload,
      id: string | number
    ): Promise<{ success?: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/members/${id}`, personalData)
      )
      return data
    },
    async getSaleOwnerChangePreview(
      id: string | number,
      newSaleOwnerId: number
    ): Promise<SaleOwnerChangePreview> {
      const { data } = await wrapApiCall(() =>
        client.get<SaleOwnerChangePreview>(`/members/${id}/sale-owner-change-preview`, {
          params: { newSaleOwnerId },
        })
      )
      return data
    },
    async updateMemberContact(
      contactData: EditMemberContactPayload,
      id: string | number
    ): Promise<{ success?: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/members/${id}/contact`, contactData)
      )
      return data
    },
    async requestChangePhoneOtp(id: string | number, phone: string): Promise<{ token: string }> {
      const { data } = await wrapApiCall(() =>
        client.post<{ token: string }>(`/members/${id}/change-phone/request-otp`, { phone })
      )
      return data
    },
    async verifyPhoneChangeOtp(
      id: string | number,
      payload: { phone: string; otpToken: string; otpPin: string }
    ): Promise<{ verificationToken: string; phone: string }> {
      const { data } = await wrapApiCall(() =>
        client.post<{ verificationToken: string; phone: string }>(
          `/members/${id}/change-phone/verify-otp`,
          payload
        )
      )
      return data
    },
    async updateBanMember(id: string | number): Promise<unknown> {
      const { data } = await wrapApiCall(() => client.patch<unknown>(`/members/${id}/ban`))
      return data
    },
    async updateUnBanMember(id: string | number): Promise<unknown> {
      const { data } = await wrapApiCall(() => client.patch<unknown>(`/members/${id}/unban`))
      return data
    },
    async getMemberGender(): Promise<MemberOption[]> {
      const { data } = await wrapApiCall(() => client.get<MemberOption[]>('/members/gender'))
      return data
    },
    async getMemberRelationship(): Promise<MemberOption[]> {
      const { data } = await wrapApiCall(() => client.get<MemberOption[]>('/members/relationship'))
      return data
    },
    async getMemberMarriedStatus(): Promise<MemberOption[]> {
      const { data } = await wrapApiCall(() =>
        client.get<MemberOption[]>('/members/married-status')
      )
      return data
    },
    async getMemberOccupation(): Promise<MemberOption[]> {
      const { data } = await wrapApiCall(() => client.get<MemberOption[]>('/members/occupation'))
      return data
    },
    async getMemberBloodType(): Promise<MemberOption[]> {
      const { data } = await wrapApiCall(() => client.get<MemberOption[]>('/members/blood-type'))
      return data
    },
    async getMemberReferralChannel(): Promise<MemberOption[]> {
      const { data } = await wrapApiCall(() =>
        client.get<MemberOption[]>('/members/referral-channel')
      )
      return data
    },
    async createMemberShipPackage(
      body: CreateMemberShipPackagePayload
    ): Promise<CreateMemberShipPackageResponse> {
      const { data } = await wrapApiCall(() =>
        client.post<CreateMemberShipPackageResponse>('/memberships', body)
      )
      return {
        success: Boolean(data?.success),
        membershipIds: Array.isArray(data?.membershipIds) ? data.membershipIds : [],
      }
    },
    async getMemberCheckInHistory(
      id: string,
      opt: PaginationOpt
    ): Promise<PaginationResponse<MemberCheckInHistory>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          `/members/${id}/check-in`,
          { params: opt }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, MemberCheckInHistory)
        ),
      }
    },
    async getMemberTrainingHistory(
      id: string,
      status: string,
      opt: PaginationOpt
    ): Promise<PaginationResponse<TrainingHistory>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          `/members/${id}/pt-history`,
          { params: { status, ...opt } }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, TrainingHistory)
        ),
      }
    },
    async getMemberClassHistory(
      id: string,
      status: string,
      opt: PaginationOpt
    ): Promise<PaginationResponse<SwimmingHistory>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          `/members/${id}/class-history`,
          { params: { status, ...opt } }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, SwimmingHistory)
        ),
      }
    },
    async getMemberSwimmingClassHistory(
      id: string,
      status: string,
      opt: PaginationOpt
    ): Promise<PaginationResponse<SwimmingHistory>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          `/members/${id}/swimming-history`,
          { params: { status, ...opt } }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, SwimmingHistory)
        ),
      }
    },
    async getMemberAggregateCount(filter: string): Promise<AggregateCount> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(
          '/postgrest/member_monthly_latest_activities/aggregate',
          {
            params: {
              count: true,
              filter,
            },
          }
        )
      )

      return deserialize(data, AggregateCount)
    },
    /**
     * Without `paging` this returns the endpoint's default page (the backend
     * caps it at 100 rows) — what the on-screen table has always shown. Pass
     * `paging` to walk the full set, e.g. for Export Data (QTY-2118).
     */
    async getMembersExpireSoon(
      from?: string,
      to?: string,
      saleFullName?: string,
      paging?: { page: number; pageSize: number }
    ): Promise<MemberMonthlyLatestActivity[]> {
      const filters: string[] = ['next_expire_date=is.not_null']
      const currentMonthYear = dayjs().format('YYYYMM')

      if (from) {
        filters.push(`next_expire_date=gte.${dayjs(from).format('YYYY-MM-DD')}`)
      }

      if (to) {
        const toNextDay = dayjs(to).add(1, 'day').format('YYYY-MM-DD')
        filters.push(`next_expire_date=lt.${toNextDay}`)
      }
      if (saleFullName) {
        filters.push(`next_expire_sale_full_name=eq.${saleFullName}`)
      }

      const query = new URLSearchParams()
      query.append('month_year', `eq.${currentMonthYear}`)

      filters.forEach((f) => {
        query.append('filter', f)
      })

      query.append('order', 'next_expire_date.asc,member_code.asc')
      if (paging) {
        query.append('page', String(paging.page))
        query.append('pageSize', String(paging.pageSize))
      }

      const { data } = await wrapApiCall(() =>
        client.get<{ data?: MemberMonthlyLatestActivity[] }>(
          `/postgrest/member_monthly_latest_activities?${query.toString()}`
        )
      )

      return data?.data ?? []
    },
    async getMemberAggregateCountWithFilters(filters: string[]): Promise<number> {
      const query = new URLSearchParams()

      query.append('count', 'true')
      filters.forEach((f) => query.append('filter', f))

      const { data } = await wrapApiCall(() =>
        client.get<{ count?: number }>(
          `/postgrest/member_monthly_latest_activities/aggregate?${query.toString()}`
        )
      )

      const raw = data?.count ?? 0
      const count = Number(raw)
      return Number.isFinite(count) ? count : 0
    },
    async getMemberTypeOverviewTotals(filters: string[] = []): Promise<{
      totalMemberType: number
      totalSessionMemberType: number
      totalDropIn: number
    }> {
      const query = new URLSearchParams()
      query.append('sum', 'total_member_type,total_session_member_type,total_drop_in')
      filters.forEach((f) => query.append('filter', f))

      const { data } = await wrapApiCall(() =>
        client.get<{ sum?: Record<string, unknown> }>(
          `/postgrest/dashboard_member_type_overview_view/aggregate?${query.toString()}`
        )
      )

      const sum = data?.sum ?? {}
      const member = Number(sum?.total_member_type ?? 0)
      const session = Number(sum?.total_session_member_type ?? 0)
      const dropin = Number(sum?.total_drop_in ?? 0)

      return {
        totalMemberType: Number.isFinite(member) ? member : 0,
        totalSessionMemberType: Number.isFinite(session) ? session : 0,
        totalDropIn: Number.isFinite(dropin) ? dropin : 0,
      }
    },
    async getAgeRangeCount(filters: string[]): Promise<number> {
      const query = new URLSearchParams()
      query.append('count', 'true')
      filters.forEach((f) => query.append('filter', f))

      const { data } = await wrapApiCall(() =>
        client.get<{ count?: number; data?: Array<{ count?: number }> }>(
          `/postgrest/member_monthly_latest_activities/aggregate?${query.toString()}`
        )
      )

      const raw = data?.count ?? data?.data?.[0]?.count ?? 0

      return Number(raw)
    },
    async getInactiveMembers(opt: PaginationOptInactive): Promise<{
      data: MemberMonthlyLatestActivity[]
      total: number
    }> {
      const page = opt.page ?? 1
      const pageSize = opt.pageSize ?? 10
      const monthFrom = opt.fromDate
        ? dayjs(opt.fromDate).startOf('month')
        : dayjs().startOf('month')
      const monthToExclusive = opt.toDate
        ? dayjs(opt.toDate).startOf('month').add(1, 'month')
        : monthFrom.add(1, 'month')

      if (monthToExclusive.isBefore(monthFrom)) {
        return { data: [], total: 0 }
      }

      const createdFrom = opt.fromDate
        ? dayjs(opt.fromDate).startOf('day')
        : monthFrom.startOf('day')
      const createdToExclusive = opt.toDate
        ? dayjs(opt.toDate).add(1, 'day').startOf('day')
        : monthToExclusive.startOf('day')

      const filters = [
        'is_inactive_more_than_14_days=eq.1',
        `month_start=gte.${monthFrom.format('YYYY-MM-DD')}`,
        `month_start=lt.${monthToExclusive.format('YYYY-MM-DD')}`,
        `member_created_at=gte.${createdFrom.format('YYYY-MM-DD')}`,
        `member_created_at=lt.${createdToExclusive.format('YYYY-MM-DD')}`,
      ]

      if (opt.memberTypeCode) {
        filters.push(`member_type_code=eq.${opt.memberTypeCode}`)
      }
      if (opt.saleFullName) {
        filters.push(`next_expire_sale_full_name=eq.${opt.saleFullName}`)
      }

      const query = new URLSearchParams()
      filters.forEach((f) => query.append('filter', f))
      query.append('page', String(page))
      query.append('pageSize', String(pageSize))
      query.append('order', 'latest_checkin_at.asc,member_code.asc')

      const { data } = await wrapApiCall(() =>
        client.get<{
          data?: MemberMonthlyLatestActivity[]
          pagination?: { total?: number }
        }>(`/postgrest/member_monthly_latest_activities?${query.toString()}`)
      )

      return {
        data: data?.data ?? [],
        total: data?.pagination?.total ?? 0,
      }
    },
    async getMostActiveMembers(opt: PaginationOptInactive): Promise<{
      data: MemberMonthlyLatestActivity[]
      total: number
    }> {
      const page = opt.page ?? 1
      const pageSize = opt.pageSize ?? 10
      const monthFrom = opt.fromDate
        ? dayjs(opt.fromDate).startOf('month')
        : dayjs().startOf('month')
      const monthToExclusive = opt.toDate
        ? dayjs(opt.toDate).startOf('month').add(1, 'month')
        : monthFrom.add(1, 'month')

      if (monthToExclusive.isBefore(monthFrom)) {
        return { data: [], total: 0 }
      }

      const query = new URLSearchParams()
      query.append('filter', `month_start=gte.${monthFrom.format('YYYY-MM-DD')}`)
      query.append('filter', `month_start=lt.${monthToExclusive.format('YYYY-MM-DD')}`)
      query.append('filter', `member_created_at=lt.${monthToExclusive.format('YYYY-MM-DD')}`)
      if (opt.saleOwnerId) {
        query.append('filter', `sale_owner_id=eq.${opt.saleOwnerId}`)
      } else if (opt.saleFullName) {
        query.append('filter', `next_expire_sale_full_name=eq.${opt.saleFullName}`)
      }
      query.append('page', String(page))
      query.append('pageSize', String(pageSize))
      query.append('order', 'member_checkin_count_in_month.desc,member_code.asc')

      const { data } = await wrapApiCall(() =>
        client.get<{
          data?: MemberMonthlyLatestActivity[]
          pagination?: { total?: number }
        }>(`/postgrest/member_monthly_latest_activities?${query.toString()}`)
      )

      return {
        data: data?.data ?? [],
        total: data?.pagination?.total ?? 0,
      }
    },
    async getTopSpenders(
      opt: PaginationOptInactive
    ): Promise<PaginationResponseMember<MemberMonthlyLatestActivity>> {
      const page = opt.page ?? 1
      const pageSize = opt.pageSize ?? 10
      const monthFrom = opt.fromDate
        ? dayjs(opt.fromDate).startOf('month')
        : dayjs().startOf('month')
      const monthToExclusive = opt.toDate
        ? dayjs(opt.toDate).startOf('month').add(1, 'month')
        : monthFrom.add(1, 'month')

      if (monthToExclusive.isBefore(monthFrom)) {
        return { data: [], total: 0 }
      }

      const query = new URLSearchParams()
      query.append('filter', `month_start=gte.${monthFrom.format('YYYY-MM-DD')}`)
      query.append('filter', `month_start=lt.${monthToExclusive.format('YYYY-MM-DD')}`)
      query.append('filter', `member_created_at=lt.${monthToExclusive.format('YYYY-MM-DD')}`)
      if (opt.memberTypeCode) {
        query.append('filter', `member_type_code=eq.${opt.memberTypeCode}`)
      }
      if (opt.saleOwnerId) {
        query.append('filter', `sale_owner_id=eq.${opt.saleOwnerId}`)
      } else if (opt.saleFullName) {
        query.append('filter', `next_expire_sale_full_name=eq.${opt.saleFullName}`)
      }
      query.append('page', String(page))
      query.append('pageSize', String(pageSize))
      query.append('order', 'monthly_spenders.desc,member_code.asc')

      const { data } = await wrapApiCall(() =>
        client.get<{
          data?: MemberMonthlyLatestActivity[]
          pagination?: { total?: number }
        }>(`/postgrest/member_monthly_latest_activities?${query.toString()}`)
      )

      return {
        data: data?.data ?? [],
        total: data?.pagination?.total ?? 0,
      }
    },
    async getTopCreditSpenders(
      opt: PaginationOptInactive
    ): Promise<PaginationResponseMember<MemberMonthlyLatestActivity>> {
      const page = opt.page ?? 1
      const pageSize = opt.pageSize ?? 10
      const monthFrom = opt.fromDate
        ? dayjs(opt.fromDate).startOf('month')
        : dayjs().startOf('month')
      const monthToExclusive = opt.toDate
        ? dayjs(opt.toDate).startOf('month').add(1, 'month')
        : monthFrom.add(1, 'month')

      if (monthToExclusive.isBefore(monthFrom)) {
        return { data: [], total: 0 }
      }

      const query = new URLSearchParams()

      query.append('filter', `month_start=gte.${monthFrom.format('YYYY-MM-DD')}`)
      query.append('filter', `month_start=lt.${monthToExclusive.format('YYYY-MM-DD')}`)
      if (opt.memberTypeCode) {
        query.append('filter', `member_type_code=eq.${opt.memberTypeCode}`)
      }
      if (opt.saleOwnerId) {
        query.append('filter', `sale_owner_id=eq.${opt.saleOwnerId}`)
      } else if (opt.saleFullName) {
        query.append('filter', `next_expire_sale_full_name=eq.${opt.saleFullName}`)
      }
      query.append('page', String(page))
      query.append('pageSize', String(pageSize))
      query.append('order', 'total_credits_used_as_of_month_end.desc,member_code.asc')

      const { data } = await wrapApiCall(() =>
        client.get<{
          data?: MemberMonthlyLatestActivity[]
          pagination?: { total?: number }
        }>(`/postgrest/member_monthly_latest_activities?${query.toString()}`)
      )

      return {
        data: data?.data ?? [],
        total: data?.pagination?.total ?? 0,
      }
    },
    async getUpcomingBirthdays(
      opt: PaginationOptInactive
    ): Promise<PaginationResponseMember<MemberMonthlyLatestActivity>> {
      const fetchPageSize = 500
      const page = opt.page ?? 1
      const pageSize = opt.pageSize ?? 10
      const startDate = opt.fromDate ? dayjs(opt.fromDate).startOf('day') : dayjs().startOf('day')
      const endDate = opt.toDate ? dayjs(opt.toDate).startOf('day') : startDate

      if (endDate.isBefore(startDate, 'day')) {
        return { data: [], total: 0 }
      }

      const birthMonths = new Set<number>()
      let monthCursor = startDate.startOf('month')
      const lastMonth = endDate.startOf('month')
      while (monthCursor.isBefore(lastMonth, 'month') || monthCursor.isSame(lastMonth, 'month')) {
        birthMonths.add(monthCursor.month() + 1)
        monthCursor = monthCursor.add(1, 'month')
      }

      const baseQuery = new URLSearchParams()
      baseQuery.append('pageSize', String(fetchPageSize))
      baseQuery.append('filter', 'date_of_birth=is.not_null')
      if (birthMonths.size > 0 && birthMonths.size < 12) {
        baseQuery.append('filter', `birth_month=in.(${Array.from(birthMonths).join(',')})`)
      }
      if (opt.saleFullName) {
        baseQuery.append('filter', `next_expire_sale_full_name=eq.${opt.saleFullName}`)
      }
      baseQuery.append('order', 'member_code.asc')

      const fetchBirthdayPage = (fetchPage: number, withCount: boolean) => {
        const query = new URLSearchParams(baseQuery)
        query.set('page', String(fetchPage))
        if (withCount) query.append('count', 'true')

        return wrapApiCall(() =>
          client.get<{
            data?: MemberMonthlyLatestActivity[]
            pagination?: { total?: number }
          }>(`/postgrest/member_birthday_summary_view?${query.toString()}`)
        )
      }

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstBirthdayData } = await fetchBirthdayPage(1, true)
      const firstBirthdayRows: MemberMonthlyLatestActivity[] = firstBirthdayData?.data ?? []

      const rawTotal = Number(firstBirthdayData?.pagination?.total ?? firstBirthdayRows.length)
      const safeTotal = Number.isFinite(rawTotal) ? rawTotal : firstBirthdayRows.length
      const totalPages = clampTotalPages(safeTotal, fetchPageSize, 'member.getUpcomingBirthdays')

      const restBirthdayRows =
        totalPages > 1
          ? await fetchPagesInBatches(
              Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
              (page) => fetchBirthdayPage(page, false).then(({ data }) => data?.data ?? [])
            )
          : []

      const allRows: MemberMonthlyLatestActivity[] = [firstBirthdayRows, ...restBirthdayRows].flat()

      const buildBirthdayForYear = (birthDate: dayjs.Dayjs, year: number) => {
        const monthIndex = birthDate.month()
        const monthFirstDay = dayjs().year(year).month(monthIndex).date(1).startOf('day')
        const safeDay = Math.min(birthDate.date(), monthFirstDay.daysInMonth())
        return monthFirstDay.date(safeDay)
      }

      const filtered = allRows
        .map((row) => {
          const birthDate = row.date_of_birth ? dayjs(row.date_of_birth) : null
          if (!birthDate || !birthDate.isValid()) {
            return null
          }

          let birthdayInRange = buildBirthdayForYear(birthDate, startDate.year())
          while (birthdayInRange.isBefore(startDate, 'day')) {
            birthdayInRange = birthdayInRange.add(1, 'year')
          }

          if (birthdayInRange.isAfter(endDate, 'day')) {
            return null
          }

          return { row, birthdayInRange }
        })
        .filter(
          (item): item is { row: MemberMonthlyLatestActivity; birthdayInRange: dayjs.Dayjs } =>
            item !== null
        )
        .sort((a, b) => {
          const diff = a.birthdayInRange.valueOf() - b.birthdayInRange.valueOf()
          if (diff !== 0) {
            return diff
          }
          return (a.row.member_code ?? '').localeCompare(b.row.member_code ?? '')
        })

      const offset = (page - 1) * pageSize
      const paginatedData = filtered.slice(offset, offset + pageSize).map((item) => item.row)

      return {
        data: paginatedData,
        total: filtered.length,
      }
    },

    async getMemberDocuments(
      memberId: string | number,
      opt?: PaginationOpt
    ): Promise<PaginationResponse<MemberDocument>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data?: MemberDocument[] }>(
          `/members/${memberId}/documents`,
          { params: opt }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data ?? [],
      }
    },

    async createMemberDocument(
      memberId: string | number,
      payload: CreateMemberDocumentPayload
    ): Promise<MemberDocument> {
      const formData = new FormData()
      formData.append('file', payload.file)
      formData.append('isVisibleToMember', String(payload.isVisibleToMember))
      formData.append('description', payload.description)
      formData.append('internalNote', payload.internalNote)
      const { data } = await wrapApiCall(() =>
        client.post<MemberDocument>(`/members/${memberId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      )
      return data
    },

    async updateMemberDocument(
      memberId: string | number,
      documentId: string,
      payload: UpdateMemberDocumentPayload
    ): Promise<MemberDocument> {
      const { data } = await wrapApiCall(() =>
        client.patch<MemberDocument>(`/members/${memberId}/documents/${documentId}`, payload)
      )
      return data
    },

    async deleteMemberDocument(
      memberId: string | number,
      documentId: string
    ): Promise<DeleteMemberDocumentResponse> {
      const { data } = await wrapApiCall(() =>
        client.delete<DeleteMemberDocumentResponse>(`/members/${memberId}/documents/${documentId}`)
      )
      return data
    },
    async getMemberFacilityHistory(
      id: string,
      status: string | null,
      opt: PaginationOpt
    ): Promise<PaginationResponse<FacilityBookedListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data?: Record<string, unknown>[] }>(
          `/members/${id}/facility-history`,
          { params: { status, ...opt } }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data:
          data?.data?.map((item: Record<string, unknown>) =>
            deserialize(item, FacilityBookedListItem)
          ) ?? [],
      }
    },
    async adjustCredit(memberId: number, body: AdjustCreditBody): Promise<void> {
      await wrapApiCall(() => client.post(`/members/${memberId}/adjust-credits`, body))
    },
  }
}
